import scrollIntoView from "scroll-into-view-if-needed";

export function scrollPageAndThumbnailIntoView(pageNumber, options = {}) {
  scrollPageIntoView(pageNumber, options);
  scrollThumbnailIntoView(pageNumber, options);
}

export function scrollPageIntoView(pageNumber, options = {}) {
  const pageNode = document.querySelector(`[data-page-number='${pageNumber}']
  `);
  if (!pageNode) return;
  scrollIntoView(pageNode, {
    behavior: options.behavior || "auto",
    block: options.block || "center",
    inline: options.inline || "center"
  });
}

export function scrollAnnotationIntoView(annotationId, options = {}) {
  const node = document.querySelector(`[data-id='${annotationId}']
  `);
  if (!node) return;

  scrollIntoView(node, {
    behavior: "auto",
    block: options.block || "center",
    inline: options.inline || "center"
  });
}

export function scrollThumbnailIntoView(pageNumber, options = {}) {
  const pageNode = document.querySelector(
    `[data-thumbnail-number='${pageNumber}']`
  );
  if (!pageNode) return;
  scrollIntoView(pageNode, {
    behavior: options.behavior || "auto",
    block: options.block || "center",
    inline: options.inline || "center"
  });
}

export const determinePDFPagePosition = (
  currentPageVisibility,
  previousPageVisibility,
  nextPageVisibility
) => {
  const onlyOnePageVisible = !nextPageVisibility && !previousPageVisibility;

  if (currentPageVisibility > 50 || onlyOnePageVisible) return "center";
  else if (nextPageVisibility) return "end";
  else if (previousPageVisibility) return "start";
};

export function getPDFTextSelection(selection) {
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);

  // Detect if this is potentially a multi-page selection
  const pages = detectPagesInSelection(range);

  // For multi-page selections, we need to split the range by page
  if (pages && pages.length > 1) {
    const pageRanges = [];

    // Process each page involved in the selection
    pages.forEach((page) => {
      const textLayer = page.node.getElementsByClassName("textLayer")[0];
      if (!textLayer) return;

      // Create a page-specific range
      const pageRange = document.createRange();
      pageRange.selectNodeContents(textLayer);

      // Find the intersection between the selection and this page
      const intersection = getIntersectionWithPage(range, textLayer);
      if (!intersection) return;

      const { startNode, startOffset, endNode, endOffset } = intersection;

      // Create a range specifically for this page's content
      const pageSpecificRange = document.createRange();
      pageSpecificRange.setStart(startNode, startOffset);
      pageSpecificRange.setEnd(endNode, endOffset);

      pageRanges.push({
        page: page.number,
        startNode,
        endNode,
        startOffset,
        endOffset,
        correctedRange: pageSpecificRange,
        selectedText: pageSpecificRange.toString()
      });
    });

    // If we have page-specific ranges, return them
    if (pageRanges.length > 0) {
      return {
        isMultiPage: true,
        pageRanges,
        correctedRange: range, // Keep the original range for reference
        originalSelection: selection
      };
    }
  }

  // For single-page selections, use the existing logic
  // Function to find the last text node in an element
  function findLastTextNodeInElement(element) {
    // Use TreeWalker to find all text nodes
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let last = null;
    let node;
    while ((node = walker.nextNode())) {
      last = node;
    }
    return last;
  }

  // Function to find the previous text node, searching through all previous siblings and their children
  function findPreviousTextNode(container, currentIndex) {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const node = container.childNodes[i];

      if (node.nodeType === Node.TEXT_NODE) {
        return node;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const textNode = findLastTextNodeInElement(node);
        if (textNode) {
          return textNode;
        }
      }
    }
    return null;
  }

  // Function to get the correct node at a specific offset
  function getNodeAtOffset(container, offset) {
    if (container.nodeType === Node.TEXT_NODE) {
      return { node: container, offset: offset };
    }

    const child = container.childNodes[offset];
    if (!child) {
      // If offset is out of bounds, return the last text node
      const lastTextNode = findPreviousTextNode(
        container,
        container.childNodes.length
      );
      return lastTextNode
        ? { node: lastTextNode, offset: lastTextNode.length }
        : null;
    }

    if (child.nodeType === Node.TEXT_NODE) {
      return { node: child, offset: 0 };
    }

    // For BR or other element nodes, get the previous text node
    const previousTextNode = findPreviousTextNode(container, offset);
    return previousTextNode
      ? { node: previousTextNode, offset: previousTextNode.length }
      : null;
  }

  // Get the actual start and end nodes with their correct offsets
  const startInfo = getNodeAtOffset(range.startContainer, range.startOffset);
  const endInfo = getNodeAtOffset(range.endContainer, range.endOffset);

  if (!startInfo || !endInfo) {
    return null;
  }

  // Create a range with the correct nodes and offsets
  const correctedRange = document.createRange();
  correctedRange.setStart(startInfo.node, startInfo.offset);
  correctedRange.setEnd(endInfo.node, endInfo.offset);

  return {
    startNode: startInfo.node,
    endNode: endInfo.node,
    startOffset: startInfo.offset,
    endOffset: endInfo.offset,
    correctedRange: correctedRange,
    selectedText: correctedRange.toString()
  };
}

// Helper function to detect all pages involved in a selection
function detectPagesInSelection(range) {
  // Find all page elements that might contain parts of the selection
  const pageElements = Array.from(
    document.querySelectorAll(".react-pdf__Page")
  );

  if (!pageElements.length) return null;

  const selectedPages = [];

  for (const pageElement of pageElements) {
    const textLayer = pageElement.querySelector(".textLayer");
    if (!textLayer) continue;

    // Test if this page intersects with the selection range
    try {
      if (range.intersectsNode(textLayer)) {
        // This page contains part of the selection
        const pageNumber = parseInt(
          pageElement.getAttribute("data-page-number"),
          10
        );
        selectedPages.push({
          number: pageNumber,
          node: pageElement
        });
      }
    } catch (e) {
      console.error("Error checking range intersection:", e);
    }
  }

  return selectedPages.length
    ? selectedPages.sort((a, b) => a.number - b.number)
    : null;
}

// Helper function to find the intersection of a range with a page text layer
function getIntersectionWithPage(range, textLayer) {
  if (
    !textLayer.contains(range.startContainer) &&
    !textLayer.contains(range.endContainer)
  ) {
    // Selection either starts and ends outside this page
    if (range.intersectsNode(textLayer)) {
      // Selection passes through this page - take the whole text layer
      return {
        startNode: getFirstTextNodeInElement(textLayer),
        startOffset: 0,
        endNode: getLastTextNodeInElement(textLayer),
        endOffset: getLastTextNodeInElement(textLayer).length
      };
    }
    return null;
  }

  if (
    textLayer.contains(range.startContainer) &&
    textLayer.contains(range.endContainer)
  ) {
    // Selection starts and ends in this page - use the exact range
    return {
      startNode: range.startContainer,
      startOffset: range.startOffset,
      endNode: range.endContainer,
      endOffset: range.endOffset
    };
  }

  if (textLayer.contains(range.startContainer)) {
    // Selection starts in this page but ends somewhere else
    return {
      startNode: range.startContainer,
      startOffset: range.startOffset,
      endNode: getLastTextNodeInElement(textLayer),
      endOffset: getLastTextNodeInElement(textLayer).length
    };
  }

  if (textLayer.contains(range.endContainer)) {
    // Selection ends in this page but starts somewhere else
    return {
      startNode: getFirstTextNodeInElement(textLayer),
      startOffset: 0,
      endNode: range.endContainer,
      endOffset: range.endOffset
    };
  }

  return null;
}

// Helper function to find the first text node in an element
function getFirstTextNodeInElement(element) {
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  const firstNode = walker.nextNode();
  return firstNode || element;
}

// Helper function to find the last text node in an element
function getLastTextNodeInElement(element) {
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );

  let lastNode = null;
  let node;
  while ((node = walker.nextNode())) {
    lastNode = node;
  }

  return lastNode || element;
}
